import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../../data/context/app-context';
import { ChatMessageItem } from '../../../../../data/data/types';
import { DeleteConversationChatMessageFileItemDocument } from '../../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../alert-message/alert-message';
import { CustomFormattedMessage } from '../../../../intl/custom-formatted-message';
import { ModalPrompt } from '../../../../modal/modal-prompt';

type ChatMessageFileDeletePromptProps = {
  messageItem: ChatMessageItem;
  file: string | null | undefined;
  onDeleted?: (value: ChatMessageItem) => void;
  onClose?: () => void;
};

export function ChatMessageFileDeletePrompt({
  messageItem,
  file,
  onDeleted,
  onClose,
}: ChatMessageFileDeletePromptProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [deleteConversationChatMessageFileItem] = useMutation(
    DeleteConversationChatMessageFileItemDocument
  );

  const onHandleDelete = useCallback(async () => {
    try {
      const result = await deleteConversationChatMessageFileItem({
        variables: {
          id: messageItem?.id ?? '',
          file: file ?? '',
        },
      });

      if (result.data?.messageFileDelete) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'conversation.chat-message-file-delete.success',
          }),
        });

        onDeleted?.(messageItem);

        onClose?.();
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    intl,
    messageItem,
    file,
    deleteConversationChatMessageFileItem,
    showAlertMessage,
    onDeleted,
    onClose,
  ]);

  return (
    <ModalPrompt
      testId="chat-message-file-delete"
      onConfirm={onHandleDelete}
      onClose={onClose}
    >
      <p>
        <CustomFormattedMessage id="conversation.chat-message-file-delete.content" />
      </p>
    </ModalPrompt>
  );
}
